import { useState } from "react";
import { MoreHorizontal, Copy, ArrowRight, Trash2, Pencil, CalendarCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MEALS } from "@/lib/foodlog";

export function EntryRow({ entry, onPatch, onDuplicate, onDelete }) {
  const { t } = useLang();
  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState(entry.quantity);
  const unit = entry.unit === "portion" ? t("j_unit_portion") : entry.unit;
  const save = async () => { if (Number(qty) > 0) await onPatch(entry.entry_id, { quantity: Number(qty) }); setEditing(false); };
  return (
    <li className="flex items-center gap-3 py-2.5" data-testid={`journal-entry-${entry.entry_id}`}>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate text-sm" data-testid="journal-entry-label">{entry.label}{entry.brand && entry.brand.toLowerCase() !== entry.label.toLowerCase() ? <span className="text-muted-foreground font-normal"> · {entry.brand}</span> : null}</p>
        <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
          {editing ? (
            <span className="inline-flex items-center gap-1"><Input type="number" step="any" min="0" value={qty} onChange={(e) => setQty(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} className="h-7 w-20 rounded-lg text-xs" data-testid="journal-entry-qty-input" autoFocus /> {unit}
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={save} data-testid="journal-entry-qty-save">OK</Button></span>
          ) : <>{entry.quantity} {unit}{entry.plan_slot && <span className="inline-flex items-center gap-0.5 ml-1 text-primary" title={t("j_from_plan")}><CalendarCheck className="h-3 w-3" />{t("j_from_plan")}</span>}</>}
        </p>
      </div>
      <p className="text-sm font-semibold shrink-0" data-testid="journal-entry-kcal">{entry.nutrition?.calories ?? "—"} {t("kcal")}</p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button className="grid place-items-center h-8 w-8 rounded-full hover:bg-secondary" aria-label="menu" data-testid="journal-entry-menu"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-2xl">
          <DropdownMenuItem onClick={() => setEditing(true)} data-testid="journal-entry-edit"><Pencil className="h-4 w-4 mr-2" />{t("j_edit_qty")}</DropdownMenuItem>
          <DropdownMenuItem onClick={() => onDuplicate(entry.entry_id)} data-testid="journal-entry-duplicate"><Copy className="h-4 w-4 mr-2" />{t("j_duplicate")}</DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="journal-entry-move"><ArrowRight className="h-4 w-4 mr-2" />{t("j_move")}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="rounded-2xl">
              {MEALS.filter((m) => m !== entry.meal).map((m) => <DropdownMenuItem key={m} onClick={() => onPatch(entry.entry_id, { meal: m })} data-testid={`journal-entry-move-${m}`}>{t(`j_${m}`)}</DropdownMenuItem>)}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem onClick={() => onDelete(entry.entry_id)} className="text-destructive" data-testid="journal-entry-delete"><Trash2 className="h-4 w-4 mr-2" />{t("j_delete")}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

export function MealSection({ meal, entries, subtotal, onAdd, actions }) {
  const { t } = useLang();
  return (
    <section className="chenia-card p-4 sm:p-5" data-testid={`journal-meal-${meal}`}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">{t(`j_${meal}`)} <span className="text-sm text-muted-foreground font-normal">· {subtotal?.calories || 0} {t("kcal")}</span></h2>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => onAdd(meal)} data-testid={`journal-add-${meal}`}>+ {t("j_add")}</Button>
      </div>
      {entries.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">{t("j_empty_meal")}</p> : (
        <ul className="mt-2 divide-y divide-border">{entries.map((e) => <EntryRow key={e.entry_id} entry={e} {...actions} />)}</ul>
      )}
    </section>
  );
}
